import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { useSessionStorage } from 'usehooks-ts';
import { Button } from '@progress/kendo-react-buttons';
import { process, State } from '@progress/kendo-data-query';
import React, { useContext, useEffect, useState } from 'react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import Toolbar from '../toolbar';
import {
  CopyTitleAsync,
  DeleteTitleAsync,
  ExportTitle,
  GetResourceValue,
  ImportTitlesAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { LinkCell } from '../../../components/grid';
import { getCurrentTimeStamp } from '../../../utils/date';
import { useToast } from '../../../contexts/toast-context';
import { TitleEntity } from '../../../types/game-document/';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { NoRecords } from '../../../components/grid/no-records';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import { downloadJSON, handleFileUpload } from '../../../utils/files';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import EditDeleteCloneCell from '../../../components/grid/edit-delete-clone-cell';
import { ExportedTitleData } from '../../../types/game-document/entities/exported-title';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export interface TitleResIds extends TitleEntity {
  titleResId?: string;
  summaryResId?: string;
  imageResId?: string;
  selected: boolean;
}

const SELECTED_FIELD = 'selected';
const Titles = () => {
  const navigate = useNavigate();
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-titles',
    initialDataState
  );

  const [titles, setTitles] = useState<TitleResIds[]>([]);
  const [selectedTitleIds, setSelectedTitleIds] = useState<string[]>([]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useState('');
  const { onToastOpen } = useToast();

  useEffect(() => {
    // Set the page title.
    document.title = `Titles - ${state.gameDocument?.name}`;
    const titles = (state.gameDocument?.assets.titles ?? []).map((title) => ({
      ...title,
      title: GetResourceValue(state.gameDocument!, title.titleResId ?? '')
    }));
    setTitles(titles as any);
  }, [state]);

  const LinkedTitleCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const DeleteCell = (props: GridCellProps) => (
    <EditDeleteCloneCell
      onEditClick={() => navigate(`${props.dataItem['id']}`)}
      onCloneClick={() => onCloneEntity(props.dataItem['id'])}
      onDeleteClick={() => setShowConfirmationDelete(props.dataItem['id'])}
      {...props}
    />
  );

  const handleJsonExport = async () => {
    if (selectedTitleIds.length === 0) {
      onToastOpen({
        title: 'Error',
        body: 'Please select the title you want to export first.',
        style: 'error'
      });

      return;
    }
    const exportedTaskContents = ExportTitle(
      state.gameDocument!,
      selectedTitleIds
    );
    const formattedTimestamp = getCurrentTimeStamp();

    downloadJSON(exportedTaskContents, `title-${formattedTimestamp}.json`);
  };

  const handleJsonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileUpload<ExportedTitleData>(
      event,
      async (exportedTitleData) => {
        ImportTitlesAsync(state.gameDocument!, exportedTitleData).then(
          (updatedGameDocument) => {
            setState((state) => UpdateGameDocState(state, updatedGameDocument));
            onToastOpen({
              title: 'Info',
              body: 'Import Success',
              style: 'info'
            });
          }
        );
      },
      (message) => {
        onToastOpen({
          title: 'Error',
          body: message,
          style: 'error'
        });
      }
    );
  };

  const onCloneEntity = (entityId: string) => {
    CopyTitleAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTitleAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setShowConfirmationDelete('');
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }
    );
  };

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem) return;
    const selectedTitleId = event.dataItem['id'];

    const updatedTitles = titles?.map((item) =>
      item.id === selectedTitleId ? { ...item, selected: !item.selected } : item
    );
    setTitles(updatedTitles);

    const taskContent = updatedTitles?.find(
      (item) => item.id === selectedTitleId
    );

    if (taskContent) {
      const updatedSelectedTaskContentIds = taskContent.selected
        ? [...selectedTitleIds, selectedTitleId]
        : selectedTitleIds.filter((id) => id !== selectedTitleId);

      setSelectedTitleIds(updatedSelectedTaskContentIds);
    }
  };

  const onHeaderSelectionChange = () => {
    const updatedTitles = titles?.map((item) => ({
      ...item,
      selected: !item.selected
    }));

    const updatedSelectedTitleIds = updatedTitles?.reduce<string[]>(
      (ids, item) => {
        if (item.selected) {
          return [...ids, item.id];
        } else {
          return ids.filter((id) => id !== item.id);
        }
      },
      [...(selectedTitleIds || [])]
    );

    setSelectedTitleIds(updatedSelectedTitleIds || []);
    setTitles(updatedTitles);
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/titles/*', text: 'Title' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Titles'}
        buttonHelpSupport={{
          title: 'Titles guide',
          url: 'https://forum.catalystglobal.com/t/4594'
        }}>
        <Button onClick={() => navigate('new')} themeColor={'primary'}>
          Add title
        </Button>
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search titles'}
          columnsToSearch={['title', 'description']}
          showCardMode={false}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          showJsonExport={true}
          handleJsonExport={handleJsonExport}
          showJsonImport={true}
          handleJsonImport={handleJsonImport}
        />
        <ExcelExport
          data={titles}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} titles.xlsx`}>
          <Grid
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            className={'cg-grid3'}
            data={process(titles ?? [], dataState)}
            dataItemKey={'id'}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: 'multiple'
            }}
            select={(titles ?? []).reduce(
              (acc, item) => ({ ...acc, [item.id]: item.selected }),
              {} as { [id: string]: boolean }
            )}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}>
            <Column
              filterable={false}
              field={SELECTED_FIELD}
              width={50}
              columnType="checkbox"
              headerSelectionValue={
                (titles?.length ?? 0) > 0 &&
                titles.every((item) => item.selected)
              }
            />
            <Column
              field={'title'}
              title={'Title'}
              cells={{ data: LinkedTitleCell }}
            />
            <Column field={'description'} title={'Description'} />
            <Column cells={{ data: DeleteCell }} />
            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(showConfirmationDelete)
              : setShowConfirmationDelete('')
          }
          onClose={() => setShowConfirmationDelete('')}>
          Are you sure you want to remove the title?
        </YesNoDialog>
      )}
    </>
  );
};

export default Titles;
